import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { apiCallWrapper, apiCallWrapperNoDispatch, Buttons, Card, Col, Container, ContainerWithPadding, defaultLabelSort, FormFieldLabel, FormFieldWrapper, Grid, PInTableList, round, Row, SectionTitle, SelectFormField, SelectOptionModel } from 'traveldesk-ui'
import { ApplicationState } from 'traveldesk-ui/dist/store'
import { ApplicationState as LocalState } from '../../store'
import { actionCreators as toursCategoriesActions } from '../../store/ToursCategories'

import ExternalIntegrationsApiService, { ApiIntegrationOption, ExternalCategoryMappingModel, ExternalCityMappingModel } from '../../services/ExternalIntegrationsApiService'
import { BookingType, BookingTypeOptions } from '../../models/OfferBookingType'
import { OfferPrivateType, OfferPrivateTypeOptions } from '../../models/OfferPrivateType'
import { CustomInputImportModel, CustomInputPer, CustomInputPerOptions, CustomInputType, ExternalTourSelectOptionModel, OfferImportModel, TourImportModel } from './TourImportModel'
import ToursApiService from '../../services/ToursApiService'
import { Link } from 'react-router-dom'
import CityMappingForm from '../../components/CityMappingForm/CityMappingForm'
import CategoryMappingForm from '../../components/CategoryMappingForm/CategoryMappingForm'

interface Props {

}

const truncate = (text: string | undefined, max: number) => {
    if (!text) return ''
    const t = text.trim()
    return t.length <= max ? t : `${t.slice(0, max)}…`
}

const formatMoney = (n: number | undefined | null) =>
    n == null || Number.isNaN(n) ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })

/** Interprets slot times as minutes from midnight (common API pattern). */
const formatMinutesOfDay = (m: number | undefined) => {
    if (m == null || m < 0) return '—'
    const h = Math.floor(m / 60) % 24
    const min = m % 60
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')} (${m})`
}

const privateTypeLabel = (v: OfferPrivateType | undefined) =>
    v === undefined ? '—' : (OfferPrivateTypeOptions.find(o => o.value === v)?.label ?? String(v))

const bookingTypeLabel = (v: BookingType | undefined) =>
    v === undefined ? '—' : (BookingTypeOptions.find(o => o.value === v)?.label ?? String(v))

const customInputTypeLabel = (v: CustomInputType | undefined) =>
    v === undefined ? '—' : (CustomInputType[v] ?? String(v))

const customInputPerLabel = (v: CustomInputPer | undefined) =>
    v === undefined ? '—' : (CustomInputPerOptions.find(o => o.value === v)?.label ?? String(v))

const OfferImportDetail: React.FC<{
    offer: OfferImportModel
    index: number
    getString: (key: string) => string
    externalCitiesHash: Map<string, string>
    onMapCity: (externalCityId: string) => void
}> = ({
    offer,
    index,
    getString,
    externalCitiesHash,
    onMapCity,
}) => {
    const title = offer.nameDisplay || offer.externalId || `${getString('Offer')} ${index + 1}`
    const slots = offer.timeSlots ?? []
    const periods = offer.pricingPeriods ?? []
    const customs = offer.customFields ?? []
    const customInputs = offer.customInputs ?? []
    const translations = offer.translations ?? []
    const origins = offer.originIds ?? []
    const externalOrigins = offer.externalOriginIds ?? []
    const externalOriginsMeta = offer.externalOriginsMeta ?? []

    return (
        <div
            style={{
                marginTop: 12,
                padding: 12,
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 6,
                background: 'rgba(0,0,0,0.02)',
            }}
        >
            <PInTableList fontSize="15px" style={{ marginBottom: 8 }}>
                <strong>{title}</strong>
            </PInTableList>
            <PInTableList fontSize="13px">
                <div><strong>{getString('External ID')}</strong>: {offer.externalId ?? '—'} {offer.isPublished ? <span style={{marginLeft:10, backgroundColor:"green", color:"white"}}>Published to Traveldesk</span> : <span style={{marginLeft:10, backgroundColor:"red", color:"white"}}>Not published</span>}</div>
                <div><strong>{getString('Private / sharing')}</strong>: {privateTypeLabel(offer.privateType)}</div>
                <div><strong>{getString('Booking type')}</strong>: {bookingTypeLabel(offer.bookingType)}</div>
                <div>
                    <strong>{getString('Guests')}</strong>: {offer.minNumOfPeople ?? '—'} – {offer.maxNumOfPeople ?? '—'}
                </div>
                <div>
                    <strong>{getString('Duration')}</strong>:{' '}
                    {offer.numberOfHours ?? 0}h {offer.numberOfMinutes ?? 0}m ·{' '}
                    <strong>{getString('Min. advance booking')}</strong> {offer.minimumAdvancedBooking ?? '—'}
                </div>
                <div>
                    <strong>{getString('UTC offset/Time zone')}</strong>: {offer.utcTimeShift ?? '—'} / {offer.timeZone ?? '—'}
                </div>
                <div>
                    <strong>{getString('Direct reporting')}</strong>:{' '}
                    {offer.isDirectReporting === undefined ? '—' : offer.isDirectReporting ? getString('Yes') : getString('No')}
                </div>
                <div>
                    <strong>{getString('Children')}</strong>:{' '}
                    {offer.childrenAllowed === undefined ? '—' : offer.childrenAllowed ? getString('Yes') : getString('No')}
                    {offer.childAge != null && ` (${getString('max age')} ${offer.childAge})`}
                </div>
                <div>
                    <strong>{getString('Infants')}</strong>:{' '}
                    {offer.infantsAllowed === undefined ? '—' : offer.infantsAllowed ? getString('Yes') : getString('No')}
                    {offer.infantAge != null && ` (${getString('max age')} ${offer.infantAge})`}
                </div>
                {(externalOriginsMeta.length > 0) && (
                    <div>
                        <strong>{getString('Origin IDs')}</strong>:{' '}
                        {externalOriginsMeta.map(origin => origin.cityId
                            ? <p key={origin.externalCityId}>
                                <span style={{ backgroundColor: 'green', color: 'white', marginRight: 4 }}>{origin.cityName} ({origin.cityId})</span>
                            </p>
                            : <p key={origin.externalCityId}>
                                <span style={{ backgroundColor: 'red', color: 'white', marginRight: 5 }}>{origin.cityName}</span>
                                <span className='clickable' style={{marginLeft:10, backgroundColor:"#33b92d", color:"white"}} onClick={() => onMapCity(origin.externalCityId)}>Map city</span>
                            </p>
                        )
                            
                        }                       
                    </div>
                )}
                {offer.notesToClientsPostBooking && (
                    <div>
                        <strong>{getString('Notes to clients (post-booking)')}</strong>: {truncate(offer.notesToClientsPostBooking, 300)}
                    </div>
                )}
                {offer.pickUpMeetingPointInformation && (
                    <div>
                        <strong>{getString('Pick-up / meeting point')}</strong>: {truncate(offer.pickUpMeetingPointInformation, 300)}
                    </div>
                )}
            </PInTableList>
            {offer.description && (
                <p style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0', fontSize: 13 }}>
                    {truncate(offer.description, 400)}
                </p>
            )}
            {offer.cancellationPolicy && offer.cancellationPolicy.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Cancellation policy')}</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                        {offer.cancellationPolicy.map((cp, i) => (
                            <li key={i}>{cp.percents ?? 0}% — {cp.hours ?? 0}h {getString('before')}</li>
                        ))}
                    </ul>
                </div>
            )}
            {customs.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Custom fields')}</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                        {customs.map((cf, i) => (
                            <li key={`${cf.fieldName}-${i}`}>
                                <strong>{cf.fieldName}</strong>: {cf.value ?? '—'}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {customInputs.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Custom inputs')}</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                        {customInputs.map((ci, i) => (
                            <li key={`${ci.externalId ?? ''}-${i}`} style={{ marginBottom: 6 }}>
                                <strong>{ci.name ?? '—'}</strong>{' '}
                                <span style={{ opacity: 0.7 }}>
                                    [{customInputTypeLabel(ci.type)}] [{customInputPerLabel(ci.per)}] [{ci.isAutocomplete ? getString('Autocomplete') : ''}] [{ci.isPaginated ? getString('Paginated') : ''}]
                                </span>
                                {ci.isRequired && <span style={{ color: '#d32f2f', marginLeft: 4 }}>*{getString('Required')}</span>}
                                {ci.isAddon && <span style={{ color: '#1976d2', marginLeft: 4 }}>({getString('Addon')})</span>}
                                {ci.description && <div style={{ opacity: 0.7 }}>{truncate(ci.description, 200)}</div>}
                                <div>
                                    {ci.minNumber != null && <span>{getString('Min')}: {ci.minNumber} </span>}
                                    {ci.maxNumber != null && <span>{getString('Max')}: {ci.maxNumber} </span>}
                                    {ci.price != null && <span>{getString('Price')}: {ci.price} </span>}
                                </div>
                                {ci.items && ci.items.length > 0 && (
                                    <ul style={{ paddingLeft: 16, margin: '2px 0 0' }}>
                                        {ci.items.map((item, j) => (
                                            <li key={`${item.value ?? ''}-${j}`}>
                                                {item.label ?? '—'} = {item.value ?? '—'}
                                                {item.price != null && ` (${getString('Price')}: ${item.price})`}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {translations.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Translations')}</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                        {translations.map((tr, i) => (
                            <li key={`${tr.languageCode}-${i}`}>
                                [{tr.languageCode}] {tr.nameDisplay ?? '—'}
                                {tr.description && ` — ${truncate(tr.description, 120)}`}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {slots.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Time slots')}</strong>
                    <table className="table table-condensed" style={{ marginTop: 6, fontSize: 12, width: '100%' }}>
                        <thead>
                            <tr>
                                <th>{getString('External ID')}</th>
                                <th>{getString('Start')}</th>
                                <th>{getString('End')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {slots.map((s, i) => (
                                <tr key={s.externalId ?? `slot-${i}`}>
                                    <td>{s.externalId ?? '—'}</td>
                                    <td>{formatMinutesOfDay(s.startTime)}</td>
                                    <td>{formatMinutesOfDay(s.endTime)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {periods.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Pricing periods')}</strong>
                    <div style={{ overflowX: 'auto', marginTop: 6 }}>
                        <table className="table table-condensed" style={{ fontSize: 11, width: '100%', minWidth: 480 }}>
                            <thead>
                                <tr>
                                    <th>{getString('From')}</th>
                                    <th>{getString('To')}</th>
                                    <th>{getString('Retail Adult')}</th>
                                    <th>{getString('Retail Child')}</th>
                                    <th>{getString('Retail Trip')}</th>                                    
                                    <th>{getString('Min Sell Adult')}</th>
                                    <th>{getString('Min Sell Child')}</th>
                                    <th>{getString('Min Sell Trip')}</th>                                    
                                    <th>{getString('Rec Adult')}</th>
                                    <th>{getString('Rec Child')}</th>
                                    <th>{getString('Rec Trip')}</th>
                                    <th>{getString('Net Adult')}</th>
                                    <th>{getString('Net Child')}</th>
                                    <th>{getString('Net Trip')}</th>
                                  
                                 
                                </tr>
                            </thead>
                            <tbody>
                                {periods.map((p, i) => (
                                    <tr key={`${p.periodStart}-${i}`}>
                                        <td>{p.periodStart ?? '—'}</td>
                                        <td>{p.periodEnd ?? '—'}</td>                                        
                                        <td>{formatMoney(p.retailPricePerAdultBaseCurrency)}</td>
                                        <td>{formatMoney(p.retailPricePerChildBaseCurrency)}</td>
                                        <td>{formatMoney(p.retailPricePerTripBaseCurrency)}</td>                                        
                                        <td>{formatMoney(p.minimumSellingPricePerAdultBaseCurrency)}</td>
                                        <td>{formatMoney(p.minimumSellingPricePerChildBaseCurrency)}</td>
                                        <td>{formatMoney(p.minimumSellingPricePerTripBaseCurrency)}</td>
                                        <td>{formatMoney(p.recPricePerAdultBaseCurrency)}</td>
                                        <td>{formatMoney(p.recPricePerChildBaseCurrency)}</td>
                                        <td>{formatMoney(p.recPricePerTripBaseCurrency)}</td>                                    
                                        <td>{formatMoney(p.netPricePerAdultBaseCurrency)}</td>
                                        <td>{formatMoney(p.netPricePerChildBaseCurrency)}</td>
                                        <td>{formatMoney(p.netPricePerTripBaseCurrency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}
const TourImportPreview: React.FC<{
    tour: TourImportModel
    getString: (key: string) => string
    toursCategoriesHash: Map<number, string>
    externalCitiesHash: Map<string, string>
    externalCategoriesHash: Map<string, ExternalCategoryMappingModel>
    toursCategoriesOptions: SelectOptionModel[]
    onSaveCityMapping: (externalCityId: string, cityId: number, cityName: string, countryId: number, onSuccessCallback: () => void) => void
    onSaveCategoryMapping: (externalCategoryId: string, categoryId: number, onSuccessCallback: () => void) => void
}> = ({ tour, getString, toursCategoriesHash, externalCitiesHash, externalCategoriesHash, toursCategoriesOptions, onSaveCityMapping, onSaveCategoryMapping }) => {
    const photoCount = tour.photos?.length ?? 0
    const offerCount = tour.offers?.length ?? 0
    const externalCategories = tour.externalCategoryIds ?? []
    const [showCityMappingForm, setShowCityMappingForm] = React.useState<string| undefined>(undefined)
    const [showCategoryMappingForm, setShowCategoryMappingForm] = React.useState<string | undefined>(undefined)
    const marginSummary = React.useMemo(() => {
        const lowStyle = {
            backgroundColor: 'red',
            color: 'white',
            padding: 4,
        }
        const mediumStyle = {
            backgroundColor: 'orange',
            color: 'white',
            padding: 4,
        }
        const highStyle = {
            backgroundColor: 'green',
            color: 'white',
            padding: 4,
        }
        const getMarginStyle = (margin: number | undefined) => {
            if (margin == null) {
                return undefined
            }
            return margin < 5 ? lowStyle : margin < 10 ? mediumStyle : highStyle
        }
        const margins =  tour.offers?.reduce((res, offer) => {
            if(offer.pricingPeriods && offer.pricingPeriods.length > 0){
                offer.pricingPeriods.forEach(period => {
                    if(period.recPricePerAdultBaseCurrency != null && period.recPricePerAdultBaseCurrency !== 0 && period.netPricePerAdultBaseCurrency != null){
                        const recMarginAdult = round((period.recPricePerAdultBaseCurrency - period.netPricePerAdultBaseCurrency)*100 / period.recPricePerAdultBaseCurrency) ?? 0
                        if(recMarginAdult < (res.minRecMaring ?? 99999)){
                            res.minRecMaring = recMarginAdult
                        }
                        if(recMarginAdult > (res.maxRecMaring ?? -99999)){
                            res.maxRecMaring = recMarginAdult
                        }
                    }
                    if(period.recPricePerTripBaseCurrency != null && period.recPricePerTripBaseCurrency !== 0 && period.netPricePerTripBaseCurrency != null){
                        const recMarginTrip = round((period.recPricePerTripBaseCurrency - period.netPricePerTripBaseCurrency)*100 / period.recPricePerTripBaseCurrency) ?? 0
                        if(recMarginTrip < (res.minRecMaring ?? 99999)){
                            res.minRecMaring = recMarginTrip
                        }
                        if(recMarginTrip > (res.maxRecMaring ?? -99999)){
                            res.maxRecMaring = recMarginTrip
                        }
                    }
                    if(period.retailPricePerAdultBaseCurrency != null && period.retailPricePerAdultBaseCurrency !== 0 && period.netPricePerAdultBaseCurrency != null){
                        const retailMarginAdult = round((period.retailPricePerAdultBaseCurrency - period.netPricePerAdultBaseCurrency)*100 / period.retailPricePerAdultBaseCurrency) ?? 0
                        if(retailMarginAdult < (res.minRetailMaring ?? 99999)){
                            res.minRetailMaring = retailMarginAdult
                        }
                        if(retailMarginAdult > (res.maxRetailMaring ?? -99999)){
                            res.maxRetailMaring = retailMarginAdult
                        }
                    }
                    if(period.retailPricePerTripBaseCurrency != null && period.retailPricePerTripBaseCurrency !== 0 && period.netPricePerTripBaseCurrency != null){
                        const retailMarginTrip = round((period.retailPricePerTripBaseCurrency - period.netPricePerTripBaseCurrency)*100 / period.retailPricePerTripBaseCurrency) ?? 0
                        if(retailMarginTrip < (res.minRetailMaring ?? 99999)){
                            res.minRetailMaring = retailMarginTrip
                        }
                        if(retailMarginTrip > (res.maxRetailMaring ?? -99999)){
                            res.maxRetailMaring = retailMarginTrip
                        }
                    }
                })            
            }
            return res
        }, {
            minRecMaring: undefined as number | undefined,
            maxRecMaring: undefined as number | undefined,
            minRetailMaring: undefined as number | undefined,
            maxRetailMaring: undefined as number | undefined,
            recMarginStyle: undefined as { backgroundColor: string; color: string; padding: number } | undefined,
            retailMarginStyle: undefined as { backgroundColor: string; color: string; padding: number } | undefined,
        })
        if(margins){
            margins.recMarginStyle = getMarginStyle(margins.minRecMaring)
            margins.retailMarginStyle = getMarginStyle(margins.minRetailMaring)
        }
        return margins
    }, [tour])

    return (
        <Card>
            {showCityMappingForm && <CityMappingForm 
                externalCityId={showCityMappingForm} 
                externalCityName={externalCitiesHash.get(showCityMappingForm) ?? ''} 
                onCancel={() => setShowCityMappingForm(undefined)} 
                onCitySelected={(cityId: number, cityName: string, countryId: number) => onSaveCityMapping(showCityMappingForm, cityId, cityName, countryId, () => setShowCityMappingForm(undefined))}
            />}
            {showCategoryMappingForm && <CategoryMappingForm
                externalCategoryId={showCategoryMappingForm}
                externalCategoryName={externalCategoriesHash.get(showCategoryMappingForm)?.apiIntegrationCategoryName ?? showCategoryMappingForm}
                categoriesOptions={toursCategoriesOptions}
                onCancel={() => setShowCategoryMappingForm(undefined)}
                onCategorySelected={(categoryId: number) => onSaveCategoryMapping(showCategoryMappingForm, categoryId, () => setShowCategoryMappingForm(undefined))}
            />}
            <h4 style={{ marginTop: 0 }}>{tour.name || getString('Untitled tour')}</h4>
            {photoCount > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, maxWidth: '100%', overflowX: 'auto', paddingBottom: 4 }}>
                    {tour.photos?.map((photo, index) => (
                        <img
                            key={`${photo}-${index}`}
                            src={photo}
                            alt={`Tour photo ${index + 1}`}
                            style={{ flex: '0 0 auto', width: 250, height: 200, objectFit: 'cover', borderRadius: 4 }}
                        />
                    ))}
                </div>
            )}
            <PInTableList fontSize="14px">
                <div><strong>{getString('External ID')}</strong>: {tour.externalId ?? '—'}</div>
                <div><strong>{getString('Last updated')}</strong>: {tour.lastEditDate ?? '—'}</div>
                <div>
                    <strong>{getString('City')}</strong>: {tour.cityId != null 
                        ? <span style={{backgroundColor:"green", color:"white"}}>{tour.cityName} ({tour.cityId})</span> 
                        : <>
                            <span style={{backgroundColor:"red", color:"white"}}>{tour.externalCityId ?? 'NOT MAPPED'}</span>
                            <span>{tour.externalCityId ? <Buttons.ButtonSmallGreen onClick={() => setShowCityMappingForm(tour.externalCityId)}>Map city</Buttons.ButtonSmallGreen> : <></>}</span>
                        </>}
                    <p style={{ fontSize: 10 }}>External City: {tour.externalCityId ? externalCitiesHash.get(tour.externalCityId) : 'NOT PROVIDED'}</p>
                </div>
                <div><strong>{getString('Currency')}</strong>: {tour.baseCurrencyCode ?? tour.externalCurrencyId ?? '—'}</div>
                {marginSummary && <div>Margin:
                    <p>Rec Price Margin: <span style={marginSummary.recMarginStyle}>{marginSummary.minRecMaring ?? '—'}{marginSummary.maxRecMaring != marginSummary.minRecMaring? <> - {marginSummary.maxRecMaring ?? '—'}</> :""} %</span></p>
                    <p>Retail Price Margin: <span style={marginSummary.retailMarginStyle}>{marginSummary.minRetailMaring ?? '—'}{marginSummary.maxRetailMaring != marginSummary.minRetailMaring? <> - {marginSummary.maxRetailMaring ?? '—'}</> :""} %</span></p>
                </div>}
                <div><strong>{getString('Location')}</strong>: {[tour.locationAddress, tour.locationTiming].filter(Boolean).join(' · ') || '—'}</div>
                {(tour.locationLatitude != null || tour.locationLongitude != null) && (
                    <div>
                        <strong>{getString('Coordinates')}</strong>:{' '}
                        {tour.locationLatitude ?? '—'}, {tour.locationLongitude ?? '—'}
                    </div>
                )}
                <div><strong>{getString('Reviews')}</strong>: {tour.customReviewCount ?? 0} ({tour.customReviewsRating ?? '—'})</div>
                <div>
                    <strong>{getString('Categories')}</strong>:{' '}
                    {externalCategories.length > 0
                        ? externalCategories.map(externalCategoryId => {
                            const categoryMapping = externalCategoriesHash.get(externalCategoryId)
                            const categoryName = categoryMapping?.apiIntegrationCategoryName ?? externalCategoryId
                            const mappedCategoryName = categoryMapping?.ourCategoryId
                                ? toursCategoriesHash.get(categoryMapping.ourCategoryId) ?? 'Unknown category'
                                : undefined

                            return mappedCategoryName
                                ? <p key={externalCategoryId}><span style={{ backgroundColor: 'green', color: 'white', marginRight: 4 }}>{mappedCategoryName} ({categoryMapping?.ourCategoryId})</span> ({categoryName})</p>
                                : <p key={externalCategoryId}>
                                    <span style={{ backgroundColor: 'red', color: 'white', marginRight: 5 }}>{categoryName}</span>
                                    <span className='clickable' style={{ marginLeft: 10, backgroundColor: '#33b92d', color: 'white' }} onClick={() => setShowCategoryMappingForm(externalCategoryId)}>Map category</span>
                                </p>
                        })
                        : '—'}
                </div>
                <div><strong>{getString('Photos')}</strong>: {photoCount}</div>
                <div><strong>{getString('Offers')}</strong>: {offerCount}</div>
            </PInTableList>
            {tour.description && (
                <div style={{ marginTop: 12 }}>
                    <strong>{getString('Description')}</strong>
                    <p style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0', fontSize: 14 }}>{truncate(tour.description, 600)}</p>
                </div>
            )}
            {tour.tourItinerary && (
                <div style={{ marginTop: 12 }}>
                    <strong>{getString('Tour Itinerary')}</strong>
                    <p style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0', fontSize: 14 }}>{truncate(tour.tourItinerary, 600)}</p>
                </div>
            )}
            {tour.cancellationPolicy && tour.cancellationPolicy.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <strong>{getString('Cancellation policy')}</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13 }}>
                        {tour.cancellationPolicy.map((cp, i) => (
                            <li key={i}>{cp.percents ?? 0}% — {cp.hours ?? 0}h {getString('before')}</li>
                        ))}
                    </ul>
                </div>
            )}
            {offerCount > 0 && tour.offers && (
                <div style={{ marginTop: 16 }}>
                    <strong style={{ fontSize: 15 }}>{getString('Offers')}</strong>
                    {tour.offers.map((offer, idx) => (
                        <OfferImportDetail
                            key={offer.externalId ?? `offer-${idx}`}
                            offer={offer}
                            index={idx}
                            getString={getString}
                            externalCitiesHash={externalCitiesHash}
                            onMapCity={setShowCityMappingForm}
                        />
                    ))}
                </div>
            )}
        </Card>
    )
}

const TOURS_ON_PAGE = 50

const getStringValue = (item: Record<string, unknown>, keys: string[]): string => {
    for (const key of keys) {
        const value = item[key]
        if (typeof value === 'string' && value.trim()) {
            return value
        }
    }
    return ''
}

const toSelectOptions = (data: unknown[] | undefined, idKeys: string[], labelKeys: string[]): SelectOptionModel[] => {
    if (!data) {
        return []
    }
    return data
        .map(item => {
            const source = item as Record<string, unknown>
            const value = getStringValue(source, idKeys)
            const label = getStringValue(source, labelKeys)
            return {
                value,
                label: label || value,
            } as SelectOptionModel
        })
        .filter(option => !!option.value)
        .sort(defaultLabelSort)
}

const ToursImportPage = (props: Props) => {
    const [apiIntegrationsOptions, setApiIntegrationsOptions] = React.useState<ApiIntegrationOption[]>([])
    const [apiIntegrationId, setApiIntegrationId] = React.useState<string>('')
    const [tours, setTours] = React.useState<ExternalTourSelectOptionModel[]>([])
    const [externalCountriesOptions, setExternalCountriesOptions] = React.useState<SelectOptionModel[]>([])
    const [externalCitiesOptions, setExternalCitiesOptions] = React.useState<SelectOptionModel[]>([])
    const [externalCitiesHash, setExternalCitiesHash] = React.useState<Map<string, string>>(new Map<string,string>())
    const [externalCategoriesOptions, setExternalCategoriesOptions] = React.useState<SelectOptionModel[]>([])
    const [externalCitiesMappings, setExternalCitiesMappings] = React.useState<ExternalCityMappingModel[]>([])
    const [externalCategoriesMappings, setExternalCategoriesMappings] = React.useState<ExternalCategoryMappingModel[]>([])
    const [selectedCountryId, setSelectedCountryId] = React.useState<string>('')
    const [selectedCityId, setSelectedCityId] = React.useState<string>('')
    const [selectedCategoryId, setSelectedCategoryId] = React.useState<string>('')
    const [currentPage, setCurrentPage] = React.useState<number>(1)
    const [totalPages, setTotalPages] = React.useState<number>(0)
    const [totalItems, setTotalItems] = React.useState<number>(0)
    const [selectedTourId, setSelectedTourId] = React.useState<string>('')
    const [selectedTourModel, setSelectedTourModel] = React.useState<TourImportModel | null>(null)    
    const [tourLoading, setTourLoading] = React.useState(false)
    const [searchTourName, setSearchTourName] = React.useState<string>('')
    const toursCategoriesOptions = useSelector((state: LocalState) => state.toursCategories.options)
    const toursCategoriesHash = React.useMemo(() => {
        return toursCategoriesOptions.reduce((acc, curr) => {
            acc.set(curr.value as number, curr.label)
            return acc
        }, new Map<number, string>())
    }, [toursCategoriesOptions])
    const externalCategoriesHash = React.useMemo(() => {
        return externalCategoriesMappings.reduce((acc, category) => {
            acc.set(category.apiIntegrationCategoryId, category)
            return acc
        }, new Map<string, ExternalCategoryMappingModel>())
    }, [externalCategoriesMappings])
    
    const onSaveCityMapping = async (externalCityId: string, cityId: number, cityName: string,countryId: number,  onSuccessCallback: () => void) => {
        const response = await apiCallWrapper<ExternalCityMappingModel>(`/api/externalintegrations/cities`,
            'POST',dispatch,{
                externalCityId: externalCityId,
                cityId: cityId,
                countryId: countryId,
                apiProviderId: apiIntegrationId
            })
        if(response.IsOk){
            setSelectedTourModel(currentTour => {
                if (!currentTour) {
                    return currentTour
                }

                const matchesExternalCity = (id: string | undefined) => String(id) === String(externalCityId)
                const res = Object.assign(TourImportModel.Create(currentTour), {                    
                    cityId: matchesExternalCity(currentTour.externalCityId) ? cityId : currentTour.cityId,
                    cityName: matchesExternalCity(currentTour.externalCityId) ? cityName : currentTour.cityName,
                    offers: currentTour.offers?.map(offer => {
                        const isMappedOrigin = offer.externalOriginIds?.some(matchesExternalCity)
                            || offer.externalOriginsMeta?.some(meta => matchesExternalCity(meta.externalCityId))

                        return OfferImportModel.Create({
                            ...offer,
                            originIds: isMappedOrigin
                                ? Array.from(new Set([...(offer.originIds ?? []), cityId]))
                                : offer.originIds,
                            externalOriginsMeta: isMappedOrigin
                                ? [
                                    ...(offer.externalOriginsMeta ?? []).filter(meta => !matchesExternalCity(meta.externalCityId)),
                                    { cityId, cityName, externalCityId },
                                ]
                                : offer.externalOriginsMeta,
                        } as OfferImportModel)
                    }),
                } as TourImportModel)
                return res
            })
            setExternalCitiesMappings(currentMappings => {
                const existingMapping = currentMappings.find(mapping => String(mapping.apiIntegrationCityId) === String(externalCityId))
                const savedMapping: ExternalCityMappingModel = response.Content ?? {
                    apiIntegrationCityId: externalCityId,
                    apiIntegrationCityName: existingMapping?.apiIntegrationCityName ?? externalCitiesHash.get(externalCityId) ?? '',
                    ourCityId: cityId,
                    ourCountryId: existingMapping?.ourCountryId,
                }

                return currentMappings.some(mapping => String(mapping.apiIntegrationCityId) === String(externalCityId))
                    ? currentMappings.map(mapping => String(mapping.apiIntegrationCityId) === String(externalCityId) ? savedMapping : mapping)
                    : [...currentMappings, savedMapping]
            })            
            onSuccessCallback()
        } else {
            alert("Failed to save city mapping")
        }
    }
    const onSaveCategoryMapping = async (externalCategoryId: string, categoryId: number, onSuccessCallback: () => void) => {
        if (!apiIntegrationId) {
            return
        }

        const response = await ExternalIntegrationsApiService.saveExternalCategoriesMapping(dispatch, {
            apiProviderId: parseInt(apiIntegrationId),
            categoryId,
            externalCategoryId,
        })
        if (response.IsOk) {
            const savedMapping: ExternalCategoryMappingModel = response.Content ?? {
                apiIntegrationCategoryId: externalCategoryId,
                apiIntegrationCategoryName: externalCategoriesHash.get(externalCategoryId)?.apiIntegrationCategoryName ?? externalCategoryId,
                ourCategoryId: categoryId,
            }
            const updatedMappings = [
                ...externalCategoriesMappings.filter(mapping => mapping.apiIntegrationCategoryId !== externalCategoryId),
                savedMapping,
            ]

            setExternalCategoriesMappings(updatedMappings)
            setSelectedTourModel(currentTour => {
                if (!currentTour) {
                    return currentTour
                }

                const mappedCategoryIds = currentTour.externalCategoryIds
                    ?.map(id => id === externalCategoryId
                        ? categoryId
                        : updatedMappings.find(mapping => mapping.apiIntegrationCategoryId === id)?.ourCategoryId)
                    .filter((id): id is number => id != null)

                return TourImportModel.Create({
                    ...currentTour,
                    toursCategories: mappedCategoryIds ? Array.from(new Set(mappedCategoryIds)) : currentTour.toursCategories,
                } as TourImportModel)
            })
            onSuccessCallback()
        } else {
            alert("Failed to save category mapping")
        }
    }

    React.useEffect(() => {
        dispatch(toursCategoriesActions.requestOptions())
    }, [])
    const Localization = useSelector((state: ApplicationState) => state.localization.locale)
    const dispatch = useDispatch()

    React.useEffect(() => {
        getApiIntegrations()
    }, [])

    React.useEffect(() => {
        setSelectedTourId('')
        setSelectedTourModel(null)
        setTours([])
        setSearchTourName('')
        setSelectedCountryId('')
        setSelectedCityId('')
        setSelectedCategoryId('')
        setCurrentPage(1)
        setTotalPages(0)
        setTotalItems(0)
        setExternalCountriesOptions([])
        setExternalCitiesOptions([])
        setExternalCategoriesOptions([])        
        setExternalCitiesMappings([])
        setExternalCategoriesMappings([])
    }, [apiIntegrationId])

    React.useEffect(() => {
        if (apiIntegrationId) {
            getFiltersData()            
        }
    }, [apiIntegrationId])

    React.useEffect(() => {
        if (apiIntegrationId) {
            getTours()
        }
    }, [apiIntegrationId, currentPage, selectedCityId, selectedCountryId, selectedCategoryId])

    React.useEffect(() => {
        if (!selectedTourId || !apiIntegrationId) {
            setSelectedTourModel(null)
            setTourLoading(false)
            return
        }
        let cancelled = false
        setTourLoading(true)
            ; (async () => {
                const selectedIntegration = apiIntegrationsOptions.find(o => String(o.value) === apiIntegrationId)
                const selectedTourOption = tours.find(t => String(t.value) === selectedTourId)
                const limitDays = selectedIntegration?.availabilityRequestLimitDays ?? 180
                const tourResponse = await ExternalIntegrationsApiService.getExternalTour(dispatch, selectedTourId, apiIntegrationId, selectedTourOption?.mappedTourId, limitDays)
                if (!cancelled) {
                    if (tourResponse.IsOk && tourResponse.Content) {
                        const tour = TourImportModel.Create(tourResponse.Content)
                        const availabilityEndDate = new Date()
                        availabilityEndDate.setDate(availabilityEndDate.getDate() + limitDays)
                        const endDateStr = availabilityEndDate.toISOString().slice(0, 10)
                        tour.offers?.forEach(offer => {
                            if (offer.pricingPeriods && offer.pricingPeriods.length > 0) {
                                const last = offer.pricingPeriods[offer.pricingPeriods.length - 1]
                                if (last.periodEnd && last.periodEnd.slice(0, 10) === endDateStr) {
                                    const farFuture = new Date(availabilityEndDate)
                                    farFuture.setFullYear(farFuture.getFullYear() + 10)
                                    last.periodEnd = farFuture.toISOString()
                                }
                            }
                        })
                        if(externalCitiesMappings){
                            tour.updateOriginsMeta(externalCitiesMappings)
                        }
                        tour.updateToursCategories(externalCategoriesMappings)
                        setSelectedTourModel(tour)
                    } else {
                        setSelectedTourModel(null)
                    }
                    setTourLoading(false)
                }
            })()
        return () => {
            cancelled = true
        }
    }, [selectedTourId, apiIntegrationId, dispatch, apiIntegrationsOptions])
    React.useEffect(() => {
        if(selectedTourModel){
            const selectedTourModelUpd = TourImportModel.Create(selectedTourModel)
            selectedTourModelUpd.updateOriginsMeta(externalCitiesMappings)
            setSelectedTourModel(selectedTourModelUpd)
        }
    }, [externalCitiesMappings])   
    React.useEffect(() => {
        if(selectedTourModel){
            const selectedTourModelUpd = TourImportModel.Create(selectedTourModel)
            selectedTourModelUpd.updateToursCategories(externalCategoriesMappings)
            setSelectedTourModel(selectedTourModelUpd)
        }
    }, [externalCategoriesMappings])

    const getApiIntegrations = async () => {
        const apiIntegrationsResponse = await ExternalIntegrationsApiService.getApiIntegrations(dispatch)
        if (apiIntegrationsResponse.IsOk && apiIntegrationsResponse.Content) {
            setApiIntegrationsOptions(apiIntegrationsResponse.Content)
        }
    }

    const getFiltersData = async () => {
        if (!apiIntegrationId) {
            return
        }
        const [countriesResponse, citiesResponse, categoriesResponse] = await Promise.all([
            ExternalIntegrationsApiService.getExternalCountries(dispatch, apiIntegrationId),
            ExternalIntegrationsApiService.getExternalCities(dispatch, apiIntegrationId),
            ExternalIntegrationsApiService.getExternalCategories(dispatch, apiIntegrationId),
        ])
        if (countriesResponse.IsOk && countriesResponse.Content) {
            setExternalCountriesOptions(countriesResponse.Content.sort(defaultLabelSort))
        }
        if (citiesResponse.IsOk && citiesResponse.Content) {
            setExternalCitiesOptions(
                toSelectOptions(citiesResponse.Content as unknown[], ['apiIntegrationCityId', 'ApiIntegrationCityId', 'id', 'value'], ['apiIntegrationCityName', 'ApiIntegrationCityName', 'name', 'label']),
            )
            setExternalCitiesMappings(citiesResponse.Content)
            setExternalCitiesHash(citiesResponse.Content.reduce((acc, item) => {
                acc.set(item.apiIntegrationCityId, item.apiIntegrationCityName)
                return acc
            }, new Map<string,string>()))
        }
        if (categoriesResponse.IsOk && categoriesResponse.Content) {
            setExternalCategoriesOptions(
                toSelectOptions(categoriesResponse.Content as unknown[], ['apiIntegrationCategoryId', 'ApiIntegrationCategoryId', 'id', 'value'], ['apiIntegrationCategoryName', 'ApiIntegrationCategoryName', 'name', 'label']),
            )
            setExternalCategoriesMappings(categoriesResponse.Content)
        }
    }
    

    const filteredTours = React.useMemo(() => {
        return tours.filter(tour => tour.label.toLowerCase().includes(searchTourName.toLowerCase()))
    }, [searchTourName, tours])

    const getTours = async () => {
        if (!apiIntegrationId) {
            return
        }
        const toursResponse = await ExternalIntegrationsApiService.getExternalTours(dispatch, apiIntegrationId, {
            page: currentPage,
            onPage: TOURS_ON_PAGE,
            cityId: selectedCityId,
            countryId: selectedCountryId,
            categoryId: selectedCategoryId,
        })
        if (toursResponse.IsOk && toursResponse.Content) {
            const toursOnPage = toursResponse.Content.entities || []
            setTours(toursOnPage)
            setTotalPages(toursResponse.Content.pagingInfo?.totalPages || 0)
            setTotalItems(toursResponse.Content.pagingInfo?.totalItems || 0)
            if (selectedTourId && !toursOnPage.some(t => String(t.value) === selectedTourId)) {
                setSelectedTourId('')
                setSelectedTourModel(null)
            }            
        } else {
            setTours([])
            setTotalPages(0)
            setTotalItems(0)
        }
    }

    const handleTourClick = (tour: SelectOptionModel) => {
        setSelectedTourId(tour.value as string)
    }

    const handleImportToTourForm = async () => {
        if (selectedTourId && apiIntegrationId && selectedTourModel) {
            const response = await ToursApiService.import(dispatch, parseInt(apiIntegrationId), selectedTourModel)
            if (response.IsOk && response.Content) {
                setTours(tours.map(t => t.value === selectedTourId ? { ...t, mappedTourId: response.Content as number} : t))                
            }
        }
    }

    const getString = (key: string) => Localization.getString(key)
    const cacheTours = async () => {
        if(confirm("Are you sure you want to cache all tours? Only run on localhost, it will take time to complete")) {
            const response = await ExternalIntegrationsApiService.cacheAllTours(dispatch, apiIntegrationId)
            if (response.IsOk && response.Content) {
                alert("Tours cached successfully")
            } else {
                alert("Failed to cache tours")
            }
        }
    }
    const selectedTourOption = React.useMemo(() => {
        return tours.find(x=>x.value === selectedTourId)
    }, [selectedTourId, tours])
    const handleTourPublish = async () => {
        if (selectedTourOption && selectedTourModel) {
            console.log(selectedTourOption)
            const response = await ToursApiService.publish(dispatch, selectedTourOption.mappedTourId as number)
            if (response.IsOk && response.Content) {
                setTours(tours.map(t => t.value === selectedTourId ? { ...t, mappedTourId: response.Content as number} : t))                
            }
        }
    }
    return <Container>
        <Grid>
            <SectionTitle title={Localization.getString('Import Tours')} />
            <Row>
                <Col isNoMargin type="100">
                    <Card>
                        <Link style={{float:"right"}} to={`/import/tours/table`}><Buttons.ButtonSmallGreen>New Table View</Buttons.ButtonSmallGreen></Link>
                        <h3>{Localization.getString('Import Tours')}</h3>                        
                        <i title="Cache all tours" className='fa fa-database clickable float-right' onClick={() => cacheTours()} />
                        <SelectFormField options={apiIntegrationsOptions} value={apiIntegrationId} onChange={(e: SelectOptionModel) => setApiIntegrationId(e.value as string)} />
                        {apiIntegrationId && (
                            <>
                                <FormFieldWrapper>
                                    <FormFieldLabel label={Localization.getString('Search Tour Name')} />
                                    <input className='form-control' value={searchTourName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTourName(e.target.value)} />
                                </FormFieldWrapper>
                                <Row>
                                    <Col type="33">
                                        <FormFieldWrapper>
                                            <FormFieldLabel label={Localization.getString('Country')} />
                                            <SelectFormField
                                                options={[{ value: '', label: Localization.getString('All countries') } as SelectOptionModel, ...externalCountriesOptions]}
                                                value={selectedCountryId}
                                                onChange={(e: SelectOptionModel) => {
                                                    setCurrentPage(1)
                                                    setSelectedCountryId((e.value as string) || '')
                                                }}
                                            />
                                        </FormFieldWrapper>
                                    </Col>
                                    <Col type="33">
                                        <FormFieldWrapper>
                                            <FormFieldLabel label={Localization.getString('City')} />
                                            <SelectFormField
                                                options={[{ value: '', label: Localization.getString('All cities') } as SelectOptionModel, ...externalCitiesOptions]}
                                                value={selectedCityId}
                                                onChange={(e: SelectOptionModel) => {
                                                    setCurrentPage(1)
                                                    setSelectedCityId((e.value as string) || '')
                                                }}
                                            />
                                        </FormFieldWrapper>
                                    </Col>
                                    <Col type="33">
                                        <FormFieldWrapper>
                                            <FormFieldLabel label={Localization.getString('Category')} />
                                            <SelectFormField
                                                options={[{ value: '', label: Localization.getString('All categories') } as SelectOptionModel, ...externalCategoriesOptions]}
                                                value={selectedCategoryId}
                                                onChange={(e: SelectOptionModel) => {
                                                    setCurrentPage(1)
                                                    setSelectedCategoryId((e.value as string) || '')
                                                }}
                                            />
                                        </FormFieldWrapper>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Card>

                </Col>
            </Row>
            {apiIntegrationId && (
                <Row>
                    <Col isNoMargin type="50">
                        <Card>
                            <PInTableList fontSize="16px">{Localization.getString('Tours')}</PInTableList>
                            {filteredTours.map(tour => (
                                <div
                                    className="clickable"
                                    style={selectedTourId === tour.value ? { background: 'rgba(0,0,0,0.06)', padding: '4px 8px', borderRadius: 4 } : { padding: '4px 8px' }}
                                    onClick={() => handleTourClick(tour)}
                                    key={tour.value?.toString()}
                                >
                                    <PInTableList style={{backgroundColor: tour.mappedTourId ? 'lightblue' : 'white'}} color='black' fontSize='14px'>
                                        {`${tour.label} (#${tour.value})`} 
                                        {tour.mappedTourId && <a href={`/tours/${tour.mappedTourId}`} target='_blank'><i className='fa fa-eye '/></a>}
                                    </PInTableList>
                                </div>
                            ))}
                            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <PInTableList fontSize="13px">
                                    {`${Localization.getString('Page')} ${currentPage}${totalPages > 0 ? ` ${Localization.getString('of')} ${totalPages}` : ''} · ${totalItems} ${Localization.getString('items')}`}
                                </PInTableList>
                                <div>
                                    <button className='button' disabled={currentPage <= 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}>
                                        {Localization.getString('Previous')}
                                    </button>
                                    <button
                                        className='button'
                                        style={{ marginLeft: 8 }}
                                        disabled={totalPages <= 0 || currentPage >= totalPages}
                                        onClick={() => setCurrentPage(prev => (totalPages > 0 ? Math.min(totalPages, prev + 1) : prev + 1))}
                                    >
                                        {Localization.getString('Next')}
                                    </button>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col isNoMargin type="50">
                        {tourLoading && (
                            <Card>
                                <PInTableList fontSize="16px">{Localization.getString('Loading…')}</PInTableList>
                            </Card>
                        )}
                        {!tourLoading && selectedTourModel && (
                            <>
                                <div style={{ marginBottom: 12 }}>
                                    {selectedTourOption && selectedTourOption.mappedTourId  ? <button className='button has-glow' onClick={handleImportToTourForm}>{Localization.getString('Update Tour')}</button> : <button className='button has-glow' onClick={handleImportToTourForm}>{Localization.getString('Import Tour')}</button>}
                                    {selectedTourOption && <a href={`/tours/${selectedTourOption.mappedTourId}`} target='_blank'><button className='button has-glow'>{Localization.getString('View Tour')}</button></a>}
                                    {(selectedTourModel && selectedTourOption && selectedTourOption.mappedTourId && !selectedTourModel.isPublished) && <button className='button has-glow' onClick={handleTourPublish}>{Localization.getString('Publish')}</button>}
                                </div>
                                <TourImportPreview
                                    tour={selectedTourModel}
                                    getString={getString}
                                    toursCategoriesHash={toursCategoriesHash}
                                    externalCitiesHash={externalCitiesHash}
                                    externalCategoriesHash={externalCategoriesHash}
                                    toursCategoriesOptions={toursCategoriesOptions}
                                    onSaveCityMapping={onSaveCityMapping}
                                    onSaveCategoryMapping={onSaveCategoryMapping}
                                />
                            </>
                        )}
                        {!tourLoading && !selectedTourModel && selectedTourId && (
                            <Card>
                                <PInTableList fontSize="16px">{Localization.getString('Could not load tour details.')}</PInTableList>
                            </Card>
                        )}
                        {!tourLoading && !selectedTourId && (
                            <Card>
                                <PInTableList fontSize="16px">{Localization.getString('Select a tour to preview')}</PInTableList>
                            </Card>
                        )}
                    </Col>
                </Row>
            )}
        </Grid>
    </Container>
}
export default ToursImportPage
